import {ShipmentCustomeo} from "../../../model/ShipmentCustomeo";
import {FormController} from "../../../forms/FormController";
import {ShipmentCustomeoNormalizer} from "../../../services/normalizer/ShipmentCustomeoNormalizer";
import CustomeoType from "../../../forms/app/types/CustomeoType";
import {useCallback, useContext, useState} from "react";
import {removeEmptyFields} from "../../../utils";
import useDatasource, {ApiError, isApiError} from "../../../hooks/useDatasource";
import {FlashContext} from "../../../context/FlashContext";
import {TokenContext} from "../../../context/TokenContext";
import axios from "axios";

interface CustomeoTypeProps {
    customeo: ShipmentCustomeo
    disabled?: boolean
    onCreateOk: (customeo: ShipmentCustomeo, index: number) => void
    index: number
}

export default function Customeo({customeo, disabled = true, onCreateOk, index}: CustomeoTypeProps) {

    const [error, setError] = useState<ApiError<ShipmentCustomeo> | null>(null);

    const {create, update} = useDatasource('shipment_customeos', ShipmentCustomeoNormalizer);

    const {addSuccess, addError} = useContext(FlashContext);
    const {token} = useContext(TokenContext);

    const createCustomeo = useCallback(
        async (data: ShipmentCustomeo) => {
            removeEmptyFields(data);

            try {
                const res = await create(data, '');

                onCreateOk(res, index);

                addSuccess('Bon de douane créé avec succès.');
            } catch (error) {
                addError('Une erreur est survenu');
                if (
                    isApiError<ShipmentCustomeo>(error)
                ) {
                    setError(error);
                } else if (
                    error
                    && typeof error === 'object'
                    && 'detail' in error
                    && typeof error.detail === 'string'
                ) {
                    addError(error.detail);
                }
            }
        }
        , [create, onCreateOk, index, addSuccess, addError]
    )

    const okClearance = useCallback(
        async (formData: ShipmentCustomeo) => {
            if (!customeo.id) {
                return;
            }

            try {
                removeEmptyFields(formData);
                const updateResult = await update({...formData, id: customeo.id}, '');

                if (!updateResult) {
                    addError('Une erreur est survenue lors de la mise à jour du formulaire.');
                    return;
                }

                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/shipment_customeos/${customeo.id}/ok-clearance`,
                    {},
                    {
                        headers: {
                            Accept: 'application/json',
                            Authorization: 'Token ' + token,
                        },
                    }
                );

                if (!response?.data) {
                    addError('Une erreur est survenue lors du passage en Ok Clearance.');
                    return;
                }

                const updated = ShipmentCustomeoNormalizer.normalize(response.data);
                onCreateOk(updated, index);
                addSuccess('Customs status mis à jour : Ok Clearance.');
            } catch (err: any) {
                if (isApiError<ShipmentCustomeo>(err?.response?.data)) {
                    setError(err.response.data);
                }
                const detail = err?.response?.data?.['hydra:description'] ?? err?.detail;
                addError(
                    typeof detail === 'string'
                        ? `Une erreur est survenue lors du passage en Ok Clearance : ${detail}`
                        : 'Une erreur est survenue lors du passage en Ok Clearance.'
                );
            }
        },
        [customeo.id, token, update, onCreateOk, index, addSuccess, addError]
    );


    return <FormController
        namespace={'shipment_customeos'}
        normalizer={ShipmentCustomeoNormalizer}
        resource={customeo}
        parentErrors={error?.violations}
        renderForm={(props) => (<CustomeoType {...props} disabled={disabled} okClearance={okClearance}/>)}
        handleSubmitEvent={() => (data) => createCustomeo(data)}
    />;
}
